import { createElement, clearNode, setHidden, focusNode } from "../shared/dom.js"
import { buildShell, button, announce } from "../shared/chrome.js"
import { maybeShuffle } from "../shared/random.js"
import { buildResult, extraKeys } from "../shared/result.js"
import { langValue } from "../validate.js"

export const TYPE = "vocab_drill"
export const OUTCOMES = ["got_it", "again", "revealed"]
export const CORRECT_OUTCOMES = ["got_it"]

const ITEM_KEYS = ["id", "prompt", "answer", "respell", "note", "variant"]

export function usableItems(exercise) {
  const items = Array.isArray(exercise?.items) ? exercise.items : []
  return items.filter((item) => langValue(item?.prompt) && langValue(item?.answer))
}

export function validate(exercise) {
  if (usableItems(exercise).length === 0) {
    return { code: "no_usable_items", message: "No vocab items have both a prompt and an answer" }
  }
  return null
}

function detail(label, value) {
  if (typeof value !== "string" || !value.trim()) return null
  return createElement("p", { class: "exw__detail" }, [
    createElement("span", { class: "exw__detail-label", text: label }),
    createElement("span", { class: "exw__detail-value", text: value })
  ])
}

export function mount(exercise, node, options) {
  const { t, now, random, callbacks } = options
  const config = exercise.config && typeof exercise.config === "object" ? exercise.config : {}
  const repeatMissed = config.repeat_missed === true

  const shell = buildShell(node, { exercise, t, modifier: "vocab-drill" })
  const attempts = []

  const queue = maybeShuffle(usableItems(exercise), config.shuffle === true, random)
    .map((item) => ({ item, requeued: false }))

  const prompt = createElement("p", { class: "exw__prompt" })
  const reveal = createElement("div", {
    class: "exw__reveal",
    "aria-live": "polite",
    "aria-atomic": "true"
  })
  const card = createElement("div", { class: "exw__card" }, [prompt, reveal])
  shell.body.appendChild(card)

  const revealBtn = button("exw__btn--ghost", t("show_answer"), "reveal")
  const gotItBtn = button("exw__btn--primary", t("got_it"), "got-it")
  const againBtn = button("exw__btn--secondary", t("again"), "again")
  shell.actions.appendChild(revealBtn)
  shell.actions.appendChild(gotItBtn)
  shell.actions.appendChild(againBtn)

  let position = 0
  let revealed = false
  let startedAt = now()
  let finished = false

  function current() {
    return queue[position]
  }

  function renderCard() {
    const entry = current()
    const item = entry.item

    prompt.textContent = item.prompt.text
    if (typeof item.prompt.lang === "string" && item.prompt.lang) {
      prompt.setAttribute("lang", item.prompt.lang)
    } else {
      prompt.removeAttribute("lang")
    }

    clearNode(reveal)
    setHidden(revealBtn, false)
    revealed = false
    startedAt = now()
    shell.progress.textContent = t("progress", { current: position + 1, total: queue.length })
  }

  function renderReveal() {
    const item = current().item
    revealed = true

    clearNode(reveal)
    reveal.appendChild(
      createElement("p", {
        class: "exw__answer",
        lang: typeof item.answer.lang === "string" && item.answer.lang ? item.answer.lang : null,
        text: item.answer.text
      })
    )

    const extras = [
      detail(t("respell_label"), item.respell),
      detail(t("note_label"), item.note),
      detail(t("variant_label"), item.variant)
    ].filter(Boolean)
    extras.forEach((extra) => reveal.appendChild(extra))

    setHidden(revealBtn, true)
    focusNode(gotItBtn)
  }

  function finish() {
    finished = true
    clearNode(shell.actions)
    clearNode(shell.body)
    shell.progress.textContent = ""

    const result = buildResult(exercise, attempts, { now, correctOutcomes: CORRECT_OUTCOMES })
    const summary = createElement("div", { class: "exw__summary" }, [
      createElement("h4", {
        class: "exw__summary-heading",
        tabindex: "-1",
        text: t("finished_vocab", { correct: result.score.correct, total: result.score.total })
      })
    ])
    shell.body.appendChild(summary)
    focusNode(summary.firstChild)
    announce(shell.status, summary.firstChild.textContent)

    if (typeof callbacks.onComplete === "function") callbacks.onComplete(result)
  }

  function advance(note) {
    position += 1
    if (position >= queue.length) {
      finish()
      return
    }
    renderCard()
    const message = t("card_announced", { current: position + 1, total: queue.length })
    announce(shell.status, note ? `${note} · ${message}` : message)
    focusNode(revealBtn)
  }

  function mark(outcome) {
    if (finished) return

    const entry = current()
    const item = entry.item
    const resolved = outcome === "got_it" && revealed ? "revealed" : outcome
    const ms = Math.max(0, now() - startedAt)

    attempts.push({
      id: item.id,
      outcome: resolved,
      ms,
      extra: extraKeys(item, ITEM_KEYS)
    })

    if (typeof callbacks.onItem === "function") {
      callbacks.onItem({ id: item.id, outcome: resolved, ms })
    }

    let note = null
    if (repeatMissed && resolved === "again" && !entry.requeued) {
      queue.push({ item, requeued: true })
      note = t("requeued")
    }

    advance(note)
  }

  const onReveal = () => {
    if (!finished && !revealed) renderReveal()
  }
  const onGotIt = () => mark("got_it")
  const onAgain = () => mark("again")

  revealBtn.addEventListener("click", onReveal)
  gotItBtn.addEventListener("click", onGotIt)
  againBtn.addEventListener("click", onAgain)

  renderCard()

  return {
    unmount() {
      revealBtn.removeEventListener("click", onReveal)
      gotItBtn.removeEventListener("click", onGotIt)
      againBtn.removeEventListener("click", onAgain)
      clearNode(node)
    }
  }
}

export default { type: TYPE, validate, mount }
